import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { SettingsSectionSkeleton } from "../components/skeleton/PageSkeletons";
import { ToastFeedback } from "../components/ToastNotifications";
import supportedContentTypes from "../config/contentTypes";
import "./Settings.css";
import { Can } from "../components/AccessContext";

const sections = [
  { id: "general", label: "General", icon: "ti-adjustments" },
  { id: "accounts", label: "Social accounts", icon: "ti-plug-connected" },
  { id: "notifications", label: "Notifications", icon: "ti-bell" },
  { id: "messages", label: "Messages & automations", icon: "ti-message-circle" },
  { id: "security", label: "Privacy & security", icon: "ti-shield-lock" },
  { id: "billing", label: "Subscription & billing", icon: "ti-credit-card" },
  { id: "integrations", label: "Data & integrations", icon: "ti-database" }
];

const platformNames = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  x: "X",
  youtube: "YouTube"
};
const contentTypes = supportedContentTypes;
const defaultGeneral = { timezone: "UTC", dateFormat: "YYYY-MM-DD", timeFormat: "12h", defaultCta: "" };
const generalFields = ["timezone", "dateFormat", "timeFormat"];
const brandFields = ["brand_tone", "brand_guidelines", "colors"];
const publishingFields = ["posting_frequency", "preferred_platforms", "preferred_content_types"];
const notificationGroups = {
  notifications: ["Posts & Publishing", "Social Accounts", "Leads", "Analytics", "Subscription & Billing", "System Alerts"],
  messages: ["Messages/DMs", "Automations"]
};
const commonTimezones = [
  "UTC", "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
  "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Berlin", "Africa/Johannesburg",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
  "Pacific/Auckland"
];
const timezoneOptions = (selected) => {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  return [...new Set([...commonTimezones, detected, selected])].sort((a, b) => a === "UTC" ? -1 : b === "UTC" ? 1 : a.localeCompare(b));
};

const normalizeProfile = (value) => ({
  brand_tone: value?.brand_tone || "Friendly",
  brand_guidelines: value?.brand_guidelines || "",
  colors: Array.isArray(value?.colors) ? value.colors : [],
  posting_frequency: Number(value?.posting_frequency) || 3,
  preferred_platforms: Array.isArray(value?.preferred_platforms) ? value.preferred_platforms : [],
  preferred_content_types: Array.isArray(value?.preferred_content_types) ? value.preferred_content_types : []
});

const Toggle = ({ checked, onChange, label, disabled = false }) => (
  <button
    className={`settings-toggle${checked ? " is-on" : ""}`}
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
  ><span /></button>
);

const Settings = () => {
  const [section, setSection] = useState("general");
  const [general, setGeneral] = useState(defaultGeneral);
  const [savedGeneral, setSavedGeneral] = useState(defaultGeneral);
  const [profile, setProfile] = useState(normalizeProfile());
  const [savedProfile, setSavedProfile] = useState(normalizeProfile());
  const [hasProfile, setHasProfile] = useState(false);
  const [preferences, setPreferences] = useState([]);
  const [savedPreferences, setSavedPreferences] = useState([]);
  const [emailSupported, setEmailSupported] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteText, setDeleteText] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletionRequest, setDeletionRequest] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const results = await Promise.allSettled([
      apiRequest("/api/settings"),
      apiRequest("/api/brand-settings/profile"),
      apiRequest("/api/notifications/preferences"),
      apiRequest("/api/social-accounts"),
      apiRequest("/api/subscriptions"),
      apiRequest("/api/settings/deletion-request")
    ]);
    const failures = [];
    if (results[0].status === "fulfilled") {
      const value = { ...defaultGeneral, ...results[0].value.preferences };
      setGeneral(value);
      setSavedGeneral(value);
    } else failures.push(`General settings: ${results[0].reason.message}`);
    if (results[1].status === "fulfilled") {
      const loaded = normalizeProfile(results[1].value.profile);
      setProfile(loaded);
      setSavedProfile(loaded);
      setHasProfile(Boolean(results[1].value.profile));
    } else failures.push(`Business profile: ${results[1].reason.message}`);
    if (results[2].status === "fulfilled") {
      setPreferences(results[2].value.categories || []);
      setSavedPreferences(results[2].value.categories || []);
      setEmailSupported(Boolean(results[2].value.emailSupported));
    } else failures.push(`Notifications: ${results[2].reason.message}`);
    if (results[3].status === "fulfilled") {
      setAccounts(results[3].value.accounts || []);
      setPlatforms(results[3].value.platforms || []);
    } else failures.push(`Social accounts: ${results[3].reason.message}`);
    if (results[4].status === "fulfilled") setSubscription(results[4].value);
    else failures.push(`Subscription: ${results[4].reason.message}`);
    if (results[5].status === "fulfilled") setDeletionRequest(results[5].value.request);
    else failures.push(`Deletion request: ${results[5].reason.message}`);
    if (failures.length) setError(failures.join(" "));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentSection = sections.find((item) => item.id === section) || sections[0];
  const generalDirty = generalFields.some((field) => general[field] !== savedGeneral[field]);
  const brandDirty = brandFields.some((field) => JSON.stringify(profile[field]) !== JSON.stringify(savedProfile[field]));
  const publishingDirty = publishingFields.some((field) => JSON.stringify(profile[field]) !== JSON.stringify(savedProfile[field])) ||
    general.defaultCta !== savedGeneral.defaultCta || general.timezone !== savedGeneral.timezone;
  const notificationCategories = notificationGroups[section] || [];
  const notificationsDirty = notificationCategories.some((category) =>
    preferences.find((item) => item.category === category)?.inAppEnabled !==
    savedPreferences.find((item) => item.category === category)?.inAppEnabled);
  const sectionDirty = section === "general"
    ? generalDirty
    : section === "brand"
      ? brandDirty
      : section === "publishing"
        ? publishingDirty
      : section === "notifications" || section === "messages"
        ? notificationsDirty
        : false;

  const updateGeneral = (key, value) => setGeneral((current) => ({ ...current, [key]: value }));
  const updateProfile = (key, value) => setProfile((current) => ({ ...current, [key]: value }));
  const toggleProfileList = (key, value) => updateProfile(key, profile[key].includes(value)
    ? profile[key].filter((item) => item !== value)
    : [...profile[key], value]);
  const resetSection = () => {
    if (section === "general") setGeneral((current) => ({ ...current, ...Object.fromEntries(generalFields.map((field) => [field, savedGeneral[field]])) }));
    if (section === "brand") {
      setProfile((current) => ({ ...current, ...Object.fromEntries(brandFields.map((field) => [field, Array.isArray(savedProfile[field]) ? [...savedProfile[field]] : savedProfile[field]])) }));
    }
    if (section === "publishing") {
      setProfile((current) => ({ ...current, ...Object.fromEntries(publishingFields.map((field) => [field, Array.isArray(savedProfile[field]) ? [...savedProfile[field]] : savedProfile[field]])) }));
      setGeneral((current) => ({ ...current, defaultCta: savedGeneral.defaultCta, timezone: savedGeneral.timezone }));
    }
    if (section === "notifications" || section === "messages") {
      const categories = notificationGroups[section];
      setPreferences((current) => current.map((item) => categories.includes(item.category)
        ? { ...item, ...savedPreferences.find((saved) => saved.category === item.category) }
        : item));
    }
    setError("");
    setSuccess("");
  };

  const saveGeneral = async () => {
    setBusy("general"); setError(""); setSuccess("");
    try {
      const result = await apiRequest("/api/settings", {
        method: "PUT",
        body: JSON.stringify({ timezone: general.timezone, dateFormat: general.dateFormat, timeFormat: general.timeFormat })
      });
      const saved = Object.fromEntries(generalFields.map((field) => [field, result.preferences[field]]));
      setGeneral((current) => ({ ...current, ...saved }));
      setSavedGeneral((current) => ({ ...current, ...saved }));
      setSuccess("General preferences saved.");
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(""); }
  };

  const saveProfile = async (fields, message) => {
    setBusy(section); setError(""); setSuccess("");
    try {
      const body = Object.fromEntries(fields.map((field) => [field, profile[field]]));
      const result = await apiRequest("/api/brand-settings/profile", { method: "PUT", body: JSON.stringify(body) });
      const saved = normalizeProfile(result.profile);
      setProfile((current) => ({ ...current, ...Object.fromEntries(fields.map((field) => [field, saved[field]])) }));
      setSavedProfile((current) => ({ ...current, ...Object.fromEntries(fields.map((field) => [field, saved[field]])) }));
      setHasProfile(true);
      setSuccess(message);
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(""); }
  };

  const savePublishing = async () => {
    setBusy("publishing"); setError(""); setSuccess("");
    try {
      if (!hasProfile) throw new Error("Complete your business profile before saving publishing defaults.");
      if (!profile.preferred_platforms.length) throw new Error("Choose at least one default social platform.");
      if (!profile.preferred_content_types.length) throw new Error("Choose at least one default content type.");
      await apiRequest("/api/brand-settings/profile", {
        method: "PUT",
        body: JSON.stringify({
          posting_frequency: profile.posting_frequency,
          preferred_platforms: profile.preferred_platforms,
          preferred_content_types: profile.preferred_content_types
        })
      });
      const savedCta = await apiRequest("/api/settings", {
        method: "PUT",
        body: JSON.stringify({ defaultCta: general.defaultCta, timezone: general.timezone })
      });
      const saved = normalizeProfile(profile);
      setSavedProfile((current) => ({ ...current, ...Object.fromEntries(publishingFields.map((field) => [field, saved[field]])) }));
      setProfile((current) => ({ ...current, ...Object.fromEntries(publishingFields.map((field) => [field, saved[field]])) }));
      setGeneral((current) => ({ ...current, defaultCta: savedCta.preferences.defaultCta, timezone: savedCta.preferences.timezone }));
      setSavedGeneral((current) => ({ ...current, defaultCta: savedCta.preferences.defaultCta, timezone: savedCta.preferences.timezone }));
      setSuccess("Publishing defaults saved.");
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(""); }
  };

  const saveNotifications = async () => {
    const selectedCategories = notificationGroups[section];
    const updates = preferences
      .filter((item) => selectedCategories.includes(item.category))
      .map(({ category, inAppEnabled }) => ({ category, inAppEnabled, emailEnabled: false }));
    if (!updates.length) return;
    setBusy(section); setError(""); setSuccess("");
    try {
      const result = await apiRequest("/api/notifications/preferences", {
        method: "PUT",
        body: JSON.stringify({ preferences: updates })
      });
      const saved = new Map((result.categories || []).map((item) => [item.category, item]));
      setPreferences((current) => current.map((item) => saved.has(item.category) ? { ...item, ...saved.get(item.category) } : item));
      setSavedPreferences((current) => current.map((item) => saved.has(item.category) ? { ...item, ...saved.get(item.category) } : item));
      setSuccess("Notification preferences saved.");
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(""); }
  };

  const saveSection = () => {
    if (section === "general") return saveGeneral();
    if (section === "brand") return saveProfile(brandFields, "Brand settings saved.");
    if (section === "publishing") return savePublishing();
    if (section === "notifications" || section === "messages") return saveNotifications();
    return undefined;
  };

  const updateNotification = (category, enabled) => setPreferences((current) => current.map((item) => item.category === category
    ? { ...item, inAppEnabled: enabled }
    : item));

  const refreshAccounts = async () => {
    setBusy("accounts"); setError("");
    try {
      const result = await apiRequest("/api/social-accounts");
      setAccounts(result.accounts || []);
      setPlatforms(result.platforms || []);
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(""); }
  };

  const connectAccount = async (platform) => {
    setBusy(platform); setError(""); setSuccess("");
    try {
      const result = await apiRequest(`/api/social-accounts/${platform}/connect`, { method: "POST", body: JSON.stringify({}) });
      window.location.assign(result.authorizationUrl);
    } catch (requestError) { setError(requestError.message); setBusy(""); }
  };

  const disconnectAccount = async (account) => {
    if (!window.confirm(`Disconnect ${account.accountName || account.platform}? You can reconnect it later.`)) return;
    setBusy(String(account.id)); setError(""); setSuccess("");
    try {
      await apiRequest(`/api/social-accounts/${account.id}`, { method: "DELETE" });
      setAccounts((current) => current.filter((item) => item.id !== account.id));
      setSuccess(`${account.accountName || account.platform} disconnected.`);
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(""); }
  };

  const exportData = async () => {
    setBusy("export"); setError(""); setSuccess("");
    try {
      const data = await apiRequest("/api/settings/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `socialyorix-workspace-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setSuccess("Your workspace data export is ready.");
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(""); }
  };

  const submitDeletionRequest = async (event) => {
    event.preventDefault();
    setBusy("deletion"); setError(""); setSuccess("");
    try {
      const result = await apiRequest("/api/settings/deletion-request", {
        method: "POST",
        body: JSON.stringify({ confirmation: deleteText })
      });
      setSuccess(result.message);
      setDeletionRequest({ status: "pending", requestedAt: result.requestedAt });
      setDeleteDialogOpen(false);
      setDeleteText("");
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(""); }
  };

  const displayedAccounts = useMemo(() => accounts.filter((item) => item.status !== "DISCONNECTED"), [accounts]);

  return (
    <section className="settings-page" aria-labelledby="settings-title">
      <header className="settings-heading">
        <div>
          <p className="settings-eyebrow">WORKSPACE PREFERENCES</p>
          <h1 id="settings-title">Settings</h1>
          <p>Make Socialyorix work the way your team does.</p>
        </div>
        <Link className="settings-profile-shortcut" to="/account/profile"><i className="ti ti-user-cog" aria-hidden="true" /> Account profile</Link>
      </header>

      {error && <ToastFeedback type="error" message={error} />}
      {success && <ToastFeedback type="success" message={success} />}

      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Settings sections">
          <p className="settings-nav-label">PREFERENCES</p>
          {sections.map((item) => <button
            key={item.id}
            className={`settings-nav-item${section === item.id ? " is-active" : ""}`}
            type="button"
            aria-current={section === item.id ? "page" : undefined}
            onClick={() => { setSection(item.id); setError(""); setSuccess(""); }}
          ><i className={`ti ${item.icon}`} aria-hidden="true" /><span>{item.label}</span><i className="ti ti-chevron-right settings-nav-arrow" aria-hidden="true" /></button>)}
        </nav>

        <div className="settings-main">
          <div className="settings-section-heading">
            <div><span className="settings-section-icon"><i className={`ti ${currentSection.icon}`} aria-hidden="true" /></span><div><h2>{currentSection.label}</h2><p>{sectionDescription(section)}</p></div></div>
            {sectionDirty && <span className="settings-unsaved"><span /> Unsaved changes</span>}
          </div>

          {loading ? <SettingsSectionSkeleton /> : (
            <div className="settings-content">
              {section === "general" && <GeneralSection general={general} update={updateGeneral} />}
              {section === "brand" && <BrandSection profile={profile} update={updateProfile} hasProfile={hasProfile} />}
              {section === "publishing" && <PublishingSection profile={profile} general={general} updateProfile={updateProfile} updateGeneral={updateGeneral} toggleList={toggleProfileList} hasProfile={hasProfile} />}
              {section === "accounts" && <AccountsSection accounts={displayedAccounts} platforms={platforms} refresh={refreshAccounts} connect={connectAccount} disconnect={disconnectAccount} busy={busy} />}
              {section === "notifications" && <NotificationSection preferences={preferences} update={updateNotification} categories={notificationGroups.notifications} emailSupported={emailSupported} />}
              {section === "messages" && <MessagesSection preferences={preferences} update={updateNotification} categories={notificationGroups.messages} emailSupported={emailSupported} />}
              {section === "security" && <SecuritySection deletionRequest={deletionRequest} deleteDialogOpen={deleteDialogOpen} setDeleteDialogOpen={setDeleteDialogOpen} deleteText={deleteText} setDeleteText={setDeleteText} submitDeletionRequest={submitDeletionRequest} busy={busy} />}
              {section === "billing" && <BillingSection data={subscription} />}
              {section === "integrations" && <IntegrationsSection accounts={displayedAccounts} exportData={exportData} busy={busy} />}
            </div>
          )}

          {["general", "brand", "publishing", "notifications", "messages"].includes(section) && !loading && <div className="settings-footer">
            <button className="settings-button settings-button-secondary" type="button" onClick={resetSection} disabled={!sectionDirty || Boolean(busy)}>Reset changes</button>
            <Can permission="user.settings.edit"><button className="settings-button settings-button-primary" type="button" onClick={saveSection} disabled={!sectionDirty || Boolean(busy) || (section === "brand" && !hasProfile) || (section === "publishing" && !hasProfile)}>
              {busy === section || (section === "publishing" && busy === "publishing") ? <><span className="settings-button-spinner" /> Saving…</> : "Save changes"}
            </button></Can>
          </div>}
        </div>
      </div>
    </section>
  );
};

function sectionDescription(id) {
  return {
    general: "Your workspace defaults for dates, time, and scheduling.",
    brand: "Brand voice and visual preferences used for content creation.",
    publishing: "Defaults used when creating and planning new content.",
    accounts: "Connection health and access for your social channels.",
    notifications: "Choose which workspace updates appear in your inbox.",
    messages: "Manage message alerts and links to supported inbox tools.",
    security: "Account access, password security, and privacy controls.",
    billing: "Your current subscription, usage, and payment records.",
    integrations: "Connected services and a portable copy of your workspace data."
  }[id];
}

function SettingCard({ title, description, children, className = "" }) {
  return <section className={`settings-card ${className}`}><div className="settings-card-heading"><h3>{title}</h3>{description && <p>{description}</p>}</div>{children}</section>;
}

function GeneralSection({ general, update }) {
  const timezones = timezoneOptions(general.timezone);
  const dateParts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
    timeZone: general.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(new Date()).map((part) => [part.type, part.value]));
  const datePreview = {
    "YYYY-MM-DD": `${dateParts.year}-${dateParts.month}-${dateParts.day}`,
    "MM/DD/YYYY": `${dateParts.month}/${dateParts.day}/${dateParts.year}`,
    "DD/MM/YYYY": `${dateParts.day}/${dateParts.month}/${dateParts.year}`
  }[general.dateFormat];
  const timePreview = new Intl.DateTimeFormat("en", {
    timeZone: general.timezone,
    hour: "numeric",
    minute: "2-digit",
    hour12: general.timeFormat === "12h"
  }).format(new Date());
  return <>
    <SettingCard title="Regional preferences" description="Set how dates and scheduling times are handled in your workspace.">
      <div className="settings-fields-grid">
        <label className="settings-field"><span>Scheduling timezone</span><select value={general.timezone} onChange={(event) => update("timezone", event.target.value)}>{timezones.map((zone) => <option key={zone} value={zone}>{zone.replaceAll("_", " ")}</option>)}</select><small>Used as your default scheduling timezone.</small></label>
        <label className="settings-field"><span>Date format</span><select value={general.dateFormat} onChange={(event) => update("dateFormat", event.target.value)}><option value="YYYY-MM-DD">YYYY-MM-DD</option><option value="MM/DD/YYYY">MM/DD/YYYY</option><option value="DD/MM/YYYY">DD/MM/YYYY</option></select></label>
        <label className="settings-field"><span>Time format</span><select value={general.timeFormat} onChange={(event) => update("timeFormat", event.target.value)}><option value="12h">12-hour (1:30 PM)</option><option value="24h">24-hour (13:30)</option></select></label>
        <div className="settings-field settings-readonly"><span>Interface language</span><strong>English</strong><small>Additional interface languages are not available yet.</small></div>
      </div>
      <p className="settings-format-preview">Preview in {general.timezone}: <strong>{datePreview}</strong> · <strong>{timePreview}</strong></p>
    </SettingCard>
    <div className="settings-info-note"><i className="ti ti-info-circle" aria-hidden="true" /><span>Timezone and format choices are saved as workspace defaults. Scheduled posts continue to use the scheduler’s existing time handling.</span></div>
  </>;
}

function BrandSection({ profile, update, hasProfile }) {
  const addColor = () => update("colors", [...profile.colors, "#087E75"]);
  const changeColor = (index, value) => update("colors", profile.colors.map((color, itemIndex) => itemIndex === index ? value : color));
  const removeColor = (index) => update("colors", profile.colors.filter((_, itemIndex) => itemIndex !== index));
  if (!hasProfile) return <ProfileRequired />;
  return <>
    <SettingCard title="Brand voice" description="The tone used by AI-assisted content generation.">
      <label className="settings-field"><span>Default brand voice</span><select value={profile.brand_tone} onChange={(event) => update("brand_tone", event.target.value)}>{["Friendly", "Professional", "Playful", "Authoritative", "Warm", "Educational"].map((tone) => <option key={tone}>{tone}</option>)}</select></label>
      <label className="settings-field settings-field-spaced"><span>Brand guidelines</span><textarea rows="4" maxLength="5000" value={profile.brand_guidelines} onChange={(event) => update("brand_guidelines", event.target.value)} placeholder="Add wording, style, and visual guidance for your content team." /><small>{profile.brand_guidelines.length}/5000 characters</small></label>
    </SettingCard>
    <SettingCard title="Brand colors" description="Up to 7 colors are shared with your business profile and content tools.">
      {profile.colors.length ? <div className="settings-colors">{profile.colors.map((color, index) => <div className="settings-color-row" key={`${index}-${color}`}><input type="color" value={/^#[\da-f]{6}$/i.test(color) ? color : "#087e75"} aria-label={`Brand color ${index + 1}`} onChange={(event) => changeColor(index, event.target.value)} /><input aria-label={`Brand color ${index + 1} hex value`} value={color} maxLength={7} onChange={(event) => changeColor(index, event.target.value)} /><button type="button" className="settings-icon-button" aria-label={`Remove brand color ${index + 1}`} onClick={() => removeColor(index)}><i className="ti ti-trash" aria-hidden="true" /></button></div>)}</div> : <p className="settings-empty-inline">No brand colors added yet.</p>}
      <button type="button" className="settings-inline-action" disabled={profile.colors.length >= 7} onClick={addColor}><i className="ti ti-plus" aria-hidden="true" /> Add a color</button>
    </SettingCard>
    <div className="settings-shortcut-row"><div><strong>Business profile and logo</strong><p>Manage business details, upload or replace your logo, and edit your profile.</p></div><Link to="/business-profile" className="settings-button settings-button-secondary">Open business profile <i className="ti ti-arrow-up-right" aria-hidden="true" /></Link></div>
  </>;
}

function PublishingSection({ profile, general, updateProfile, updateGeneral, toggleList, hasProfile }) {
  if (!hasProfile) return <ProfileRequired />;
  return <>
    <SettingCard title="Default publishing platforms" description="Preselected when generating content. You can still change platforms per post.">
      <div className="settings-choice-grid">{Object.entries(platformNames).map(([key, name]) => <label className="settings-choice" key={key}><input type="checkbox" checked={profile.preferred_platforms.includes(key)} onChange={() => toggleList("preferred_platforms", key)} /><span>{name}</span></label>)}</div>
    </SettingCard>
    <SettingCard title="Posting cadence" description="The default weekly frequency used for AI content plans.">
      <label className="settings-field"><span>Target posts per week</span><select value={profile.posting_frequency} onChange={(event) => updateProfile("posting_frequency", Number(event.target.value))}>{[1, 2, 3, 4, 5, 6, 7].map((count) => <option value={count} key={count}>{count} {count === 1 ? "post" : "posts"} per week</option>)}</select></label>
    </SettingCard>
    <SettingCard title="Scheduling timezone" description="Saved as the default timezone preference for your workspace.">
      <label className="settings-field"><span>Timezone</span><select value={general.timezone} onChange={(event) => updateGeneral("timezone", event.target.value)}>{timezoneOptions(general.timezone).map((zone) => <option key={zone} value={zone}>{zone.replaceAll("_", " ")}</option>)}</select></label>
    </SettingCard>
    <SettingCard title="Default content types" description="Content categories suggested when planning new posts.">
      <div className="settings-choice-grid">{contentTypes.map((type) => <label className="settings-choice" key={type}><input type="checkbox" checked={profile.preferred_content_types.includes(type)} onChange={() => toggleList("preferred_content_types", type)} /><span>{type}</span></label>)}</div>
    </SettingCard>
    <SettingCard title="Default call to action" description="Included as a preference for content generation; review it before publishing.">
      <label className="settings-field"><span>Call to action</span><input type="text" maxLength={180} value={general.defaultCta} onChange={(event) => updateGeneral("defaultCta", event.target.value)} placeholder="e.g. Explore the collection" /><small>{general.defaultCta.length}/180 characters</small></label>
    </SettingCard>
    <div className="settings-info-note"><i className="ti ti-info-circle" aria-hidden="true" /><span>Scheduled post timezone uses the scheduler’s current behavior. Approval workflows aren’t available in this workspace yet.</span></div>
  </>;
}

function ProfileRequired() {
  return <div className="settings-empty-state"><span><i className="ti ti-building-store" aria-hidden="true" /></span><h3>Set up your business profile first</h3><p>Your brand and publishing defaults are stored with your business profile.</p><Link className="settings-button settings-button-primary" to="/business-profile">Open business profile</Link></div>;
}

function AccountsSection({ accounts, platforms, refresh, connect, disconnect, busy }) {
  const configuredPlatforms = platforms.filter((item) => item.configured);
  return <>
    <SettingCard title="Connected accounts" description="Account access is managed securely by the social platform. Tokens and credentials are never shown here.">
      <div className="settings-card-actions"><span className="settings-count"><span className="settings-status-dot" />{accounts.filter((account) => account.status === "CONNECTED").length} connected</span><button className="settings-inline-action" type="button" onClick={refresh} disabled={Boolean(busy)}><i className={`ti ${busy === "accounts" ? "ti-loader-2 settings-spin" : "ti-refresh"}`} aria-hidden="true" /> Refresh</button></div>
      {accounts.length ? <div className="settings-account-list">{accounts.map((account) => {
        const expiring = account.tokenStatus === "EXPIRING" || account.tokenStatus === "EXPIRED" || account.status === "EXPIRED";
        return <article className="settings-account-row" key={account.id}>
          <span className={`settings-platform-icon tone-${account.platform}`}><i className={`ti ${platformIcon(account.platform)}`} aria-hidden="true" /></span>
          <div className="settings-account-details"><strong>{account.accountName || account.username || platformNames[account.platform] || account.platform}</strong><span>{platformNames[account.platform] || account.platform} · {account.username ? `@${account.username}` : account.tokenStatus === "NO_EXPIRY" ? "No expiry reported" : "Connected account"}</span></div>
          <span className={`settings-status-pill${expiring ? " is-warning" : ""}`}>{expiring ? account.tokenStatus === "EXPIRED" ? "Reconnect required" : "Expiring soon" : account.status === "CONNECTED" ? "Connected" : account.status}</span>
          {account.tokenExpiresAt && <span className="settings-token-expiry">Expires {new Date(account.tokenExpiresAt).toLocaleDateString()}</span>}
          {account.status === "EXPIRED" && <Can permission="user.social_accounts.add"><button className="settings-text-action" type="button" onClick={() => connect(account.platform)} disabled={Boolean(busy)}>Reconnect</button></Can>}
          <Can permission="user.social_accounts.delete"><button className="settings-text-action" type="button" onClick={() => disconnect(account)} disabled={Boolean(busy)}>Disconnect</button></Can>
        </article>;
      })}</div> : <div className="settings-empty-inline">No social accounts connected yet.</div>}
    </SettingCard>
    <SettingCard title="Available connections" description="Only providers configured by your workspace administrator are available to connect.">
      {configuredPlatforms.length ? <div className="settings-connect-list">{configuredPlatforms.map((item) => {
        const connected = accounts.some((account) => account.platform === item.platform);
        return <div className="settings-connect-row" key={item.platform}><span>{platformNames[item.platform] || item.platform.replaceAll("_", " ")}</span>{connected ? <span className="settings-muted-status">Already connected</span> : <Can permission="user.social_accounts.add"><button type="button" className="settings-button settings-button-secondary" onClick={() => connect(item.platform)} disabled={Boolean(busy)}>{busy === item.platform ? "Connecting…" : "Connect"}</button></Can>}</div>;
      })}</div> : <p className="settings-empty-inline">No additional social providers are configured. Add provider credentials in administrator settings before connecting an account.</p>}
    </SettingCard>
  </>;
}

function platformIcon(platform) {
  return { instagram: "ti-brand-instagram", facebook: "ti-brand-facebook", linkedin: "ti-brand-linkedin", x: "ti-brand-x", youtube: "ti-brand-youtube", tiktok: "ti-brand-tiktok", pinterest: "ti-brand-pinterest", threads: "ti-at", google_business: "ti-brand-google-maps" }[platform] || "ti-world";
}

function NotificationSection({ preferences, update, categories, emailSupported }) {
  return <>
    <NotificationPreferences preferences={preferences} update={update} categories={categories} emailSupported={emailSupported} />
    <div className="settings-info-note"><i className="ti ti-info-circle" aria-hidden="true" /><span>Email delivery is not configured for this workspace, so email notifications are unavailable. In-app preferences are saved to this business.</span></div>
  </>;
}

function MessagesSection({ preferences, update, categories, emailSupported }) {
  return <>
    <NotificationPreferences preferences={preferences} update={update} categories={categories} emailSupported={emailSupported} />
    <SettingCard title="Messaging tools" description="Use the existing inbox and automation workspace to manage conversations and rules.">
      <div className="settings-shortcut-row"><div><strong>Messages and DM automations</strong><p>Review conversations, saved replies, and currently supported automation rules.</p></div><Link className="settings-button settings-button-secondary" to="/messages">Open messages <i className="ti ti-arrow-up-right" aria-hidden="true" /></Link></div>
    </SettingCard>
  </>;
}

function NotificationPreferences({ preferences, update, categories, emailSupported }) {
  const items = preferences.filter((item) => categories.includes(item.category));
  return <SettingCard title="Notification channels" description="Choose how each type of workspace activity is delivered.">
    <div className="settings-preference-header"><span>Activity</span><span>In-app</span><span>Email</span></div>
    {items.map((item) => <div className="settings-preference-row" key={item.category}>
      <div><strong>{item.category}</strong><small>{notificationDescription(item.category)}</small></div>
      <Toggle checked={item.inAppEnabled} onChange={(enabled) => update(item.category, enabled)} label={`In-app notifications for ${item.category}`} />
      <Toggle checked={item.emailEnabled} onChange={() => {}} label={`Email notifications for ${item.category}`} disabled={!emailSupported} />
    </div>)}
    {!items.length && <p className="settings-empty-inline">Notification preferences are unavailable.</p>}
    {!emailSupported && <p className="settings-caption">Email notifications are unavailable because email delivery is not configured.</p>}
  </SettingCard>;
}

function notificationDescription(category) {
  return {
    "Posts & Publishing": "Publishing status and scheduled content updates",
    "Social Accounts": "Connection and account access changes",
    "Messages/DMs": "Incoming messages and conversation activity",
    Automations: "Automation runs and failures",
    Leads: "New lead activity and form submissions",
    Analytics: "Workspace analytics updates",
    "Subscription & Billing": "Plan, renewal, and payment updates",
    "System Alerts": "Important service and workspace alerts"
  }[category] || "Workspace updates";
}

function SecuritySection({ deletionRequest, deleteDialogOpen, setDeleteDialogOpen, deleteText, setDeleteText, submitDeletionRequest, busy }) {
  return <>
    <SettingCard title="Account security" description="Keep your account protected and up to date.">
      <div className="settings-security-row"><span className="settings-security-icon"><i className="ti ti-key" aria-hidden="true" /></span><div><strong>Password</strong><p>Update your password using your current password.</p></div><Link to="/account/profile" className="settings-button settings-button-secondary">Change password <i className="ti ti-arrow-up-right" aria-hidden="true" /></Link></div>
      <div className="settings-security-row"><span className="settings-security-icon"><i className="ti ti-device-desktop" aria-hidden="true" /></span><div><strong>Active sessions</strong><p>Session listing and remote sign-out are not supported yet.</p></div><span className="settings-unavailable">Not available</span></div>
    </SettingCard>
    <SettingCard title="Request account deletion" description="This sends a deletion request for review. It does not immediately remove your account or workspace data." className="settings-danger-card">
      {deletionRequest ? <div className="settings-shortcut-row"><div><strong>Deletion request submitted</strong><p>Your request was recorded on {new Date(deletionRequest.requestedAt).toLocaleDateString()}. The account has not been deleted.</p></div><span className="settings-status-pill is-warning">Submitted</span></div> : !deleteDialogOpen ? <div className="settings-shortcut-row"><div><strong>Account and workspace data</strong><p>You’ll need to explicitly confirm the request before it is recorded.</p></div><Can permission="user.settings.delete"><button className="settings-button settings-button-danger" type="button" onClick={() => setDeleteDialogOpen(true)}>Request deletion</button></Can></div> : <form className="settings-delete-form" onSubmit={submitDeletionRequest}>
        <p>Type <strong>DELETE MY ACCOUNT</strong> to confirm your account deletion request.</p>
        <label className="settings-field"><span>Confirmation phrase</span><input autoComplete="off" value={deleteText} onChange={(event) => setDeleteText(event.target.value)} required /></label>
        <div className="settings-action-row"><button className="settings-button settings-button-secondary" type="button" onClick={() => { setDeleteDialogOpen(false); setDeleteText(""); }}>Cancel</button><button className="settings-button settings-button-danger" type="submit" disabled={deleteText !== "DELETE MY ACCOUNT" || Boolean(busy)}>{busy === "deletion" ? "Submitting…" : "Confirm request"}</button></div>
      </form>}
    </SettingCard>
  </>;
}

function BillingSection({ data }) {
  if (!data) return <div className="settings-empty-state"><span><i className="ti ti-credit-card" aria-hidden="true" /></span><h3>Billing details unavailable</h3><p>We couldn’t load subscription information. Refresh the page to try again.</p></div>;
  const money = (amount, currency) => new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(Number(amount || 0) / 100);
  return <>
    <SettingCard title="Current plan" description="Your subscription and billing period.">
      <div className="settings-plan-card"><div><span className="settings-plan-label">ACTIVE PLAN</span><h3>{data.current?.plan?.name || "Current plan"}</h3><p>{data.current?.status?.replaceAll("_", " ")} · {data.current?.billingInterval || "monthly"} billing</p>{data.current?.currentPeriodEnd && <small>Next renewal {new Date(data.current.currentPeriodEnd).toLocaleDateString()}</small>}</div><Link to="/upgrade-plan" className="settings-button settings-button-primary">Manage plan <i className="ti ti-arrow-up-right" aria-hidden="true" /></Link></div>
    </SettingCard>
    <SettingCard title="Plan usage" description="Current usage compared with your plan limits.">
      <div className="settings-usage-grid">{Object.entries(data.usage || {}).map(([key, item]) => <div className="settings-usage-item" key={key}><span>{key.replaceAll(/([A-Z])/g, " $1").replaceAll("_", " ")}</span><strong>{item.used} <small>/ {item.limit}</small></strong><div className="settings-usage-track"><span style={{ width: `${item.limit ? Math.min(100, (item.used / item.limit) * 100) : 0}%` }} /></div></div>)}</div>
    </SettingCard>
    <SettingCard title="Payment history" description="Verified payments from your subscription account.">
      {data.payments?.length ? <div className="settings-payment-list">{data.payments.slice(0, 10).map((payment) => <div className="settings-payment-row" key={payment.id}><div><strong>{money(payment.amount, payment.currency)}</strong><span>{new Date(payment.createdAt).toLocaleDateString()}</span></div><span className={`settings-status-pill${payment.status === "captured" ? "" : " is-warning"}`}>{payment.status}</span></div>)}</div> : <p className="settings-empty-inline">Your verified payment history will appear here.</p>}
      <p className="settings-caption">Billing details and payment methods are managed securely through the subscription provider.</p>
    </SettingCard>
  </>;
}

function IntegrationsSection({ accounts, exportData, busy }) {
  return <>
    <SettingCard title="Connected integrations" description="Services currently connected to your business workspace.">
      {accounts.length ? <div className="settings-integration-list">{accounts.map((account) => <div className="settings-integration-row" key={account.id}><span className="settings-platform-icon"><i className={`ti ${platformIcon(account.platform)}`} aria-hidden="true" /></span><div><strong>{account.accountName || platformNames[account.platform] || account.platform}</strong><small>{platformNames[account.platform] || account.platform} · {account.status === "CONNECTED" ? "Connected" : account.status}</small></div><span className={`settings-status-pill${account.status === "CONNECTED" ? "" : " is-warning"}`}>{account.status === "CONNECTED" ? "Connected" : account.status}</span></div>)}</div> : <p className="settings-empty-inline">No integrations are connected. Configure social channels from Social Accounts.</p>}
      <Link className="settings-inline-action" to="/social-accounts">Manage social accounts <i className="ti ti-arrow-up-right" aria-hidden="true" /></Link>
    </SettingCard>
    <SettingCard title="Export workspace data" description="Download your business profile, account preferences, notification history, social connection metadata, and billing records. Social credentials are never included.">
      <div className="settings-shortcut-row"><div><strong>Portable JSON export</strong><p>Your export is generated securely for your signed-in account.</p></div><Can permission="user.settings.export"><button className="settings-button settings-button-secondary" type="button" onClick={exportData} disabled={Boolean(busy)}>{busy === "export" ? "Preparing export…" : "Download data"} <i className="ti ti-download" aria-hidden="true" /></button></Can></div>
    </SettingCard>
  </>;
}

export default Settings;
